import { Checkbox, Icon, Typography } from "@mindlercare/ui-web";
import { styled } from "styled-components";

import { useIcbtSelector } from "../IcbtSelectorContext";
import { useIcbtTranslation } from "../translations";
import { List, ListItemSubtitle, ModalContentColumn } from "../common";
import type { EnrichedProgram } from "../types";

type ProgramColumnProps = {
  programs: EnrichedProgram[];
  programsToAssignForSelectedTreatment: string[];
  onProgramSelectionChanged: (programId: string, treatmentId?: string) => void;
  isPreviewOnly?: boolean;
  assigningEnabled?: boolean;
  onProgramPreviewSelect?: (programId: string) => void;
};

const ProgramColumn = ({
  programs,
  programsToAssignForSelectedTreatment,
  onProgramSelectionChanged,
  isPreviewOnly,
  assigningEnabled = true,
  onProgramPreviewSelect,
}: ProgramColumnProps) => {
  if (programs.length === 0) {
    return (
      <ModalContentColumn leftPadding={18}>
        <List />
      </ModalContentColumn>
    );
  }

  const programsWithSelectedAssignments = programs.map((program) => ({
    ...program,
    selectedToBeAssigned: programsToAssignForSelectedTreatment.includes(
      program.id,
    ),
  }));

  return (
    <ModalContentColumn leftPadding={18}>
      <List>
        {programsWithSelectedAssignments.map((program, index) => (
          <ProgramListItem
            key={program.id}
            program={program}
            checked={program.selectedToBeAssigned}
            index={index}
            isPreviewOnly={isPreviewOnly}
            assigningEnabled={assigningEnabled}
            onCheckboxClick={() => onProgramSelectionChanged(program.id)}
            onProgramPreviewSelect={onProgramPreviewSelect}
          />
        ))}
      </List>
    </ModalContentColumn>
  );
};

export default ProgramColumn;

// --- ProgramListItem ---

type ProgramListItemProps = {
  program: EnrichedProgram;
  checked: boolean;
  index: number;
  isPreviewOnly?: boolean;
  assigningEnabled?: boolean;
  onCheckboxClick: () => void;
  onProgramPreviewSelect?: (programId: string) => void;
};

const ProgramListItem = ({
  onCheckboxClick,
  checked,
  program,
  index,
  isPreviewOnly,
  assigningEnabled = true,
  onProgramPreviewSelect,
}: ProgramListItemProps) => {
  const t = useIcbtTranslation();
  const { selectedProgramId, setSelectedProgramId } = useIcbtSelector();

  const determineSubtitle = () => {
    if (!program.assigned && !program.started) return undefined;
    if (program.isReassignable) return t.REASSIGN;

    const isSelfExplored = !program.assigned && program.started;
    let subStatus: string | undefined;
    if (isSelfExplored) {
      subStatus = t.INITIATED_BY_PATIENT.toLowerCase();
    }

    let status: string;
    if (program.completed) {
      status = t.COMPLETED;
    } else if (program.started) {
      status = t.IN_PROGRESS;
    } else {
      status = t.NOT_STARTED;
    }

    return subStatus ? `${status} - ${subStatus}` : status;
  };

  const subtitle = determineSubtitle();

  const checkboxDisabledFromStatus =
    program.assigned && !program.isReassignable;

  const checkedFromStatus = program.assigned && !program.isReassignable;

  return (
    <ProgramItem
      isPreviewOnly={isPreviewOnly}
      data-testid={`programLink_${program.id}`}
      onClick={() => {
        setSelectedProgramId(program.id);
        onProgramPreviewSelect?.(program.id);
      }}
      selected={selectedProgramId === program.id}
    >
      <ListItemInnerContainer>
        <CheckBoxAndTextContainer>
          {!isPreviewOnly && (
            <Checkbox
              name={`program-${program.id}`}
              value={program.id}
              label=""
              disabled={!assigningEnabled || checkboxDisabledFromStatus}
              checked={checked || checkedFromStatus}
              onChange={(e) => {
                e.stopPropagation();
                onCheckboxClick();
              }}
            />
          )}
          <TextContainer>
            <Typography insensitive variant="body" className="programTitle">
              {!program.standalone && (
                <span className="indexNumber">{index + 1}.</span>
              )}{" "}
              <span>{program.title}</span>
            </Typography>
            {subtitle && !isPreviewOnly && (
              <SubtitleContainer>
                <ListItemSubtitle>{subtitle}</ListItemSubtitle>
              </SubtitleContainer>
            )}
          </TextContainer>
        </CheckBoxAndTextContainer>
        <div>
          <Icon.ArrowRight />
        </div>
      </ListItemInnerContainer>
    </ProgramItem>
  );
};

// --- Styled components ---

export const ProgramItem = styled.div<{
  selected: boolean;
  isPreviewOnly?: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: ${({ isPreviewOnly }) =>
    isPreviewOnly ? "8px 16px" : "4px 20px 4px 16px"};
  align-items: center;
  cursor: pointer;
  width: auto;
  &:hover {
    background-color: ${({ theme }) => theme.COLORS.GRAY_X_LIGHT};
    .programTitle span:not(.indexNumber) {
      text-decoration: underline;
    }
  }
  border-radius: 2px;
  background-color: ${({ selected, theme }) =>
    selected ? theme.COLORS.LIGHT_ACTION_BLUE : "inherit"};
`;

const ListItemInnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 24px;
  justify-content: space-between;
  align-items: center;
`;

const CheckBoxAndTextContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 0;
  overflow: visible;
`;

const TextContainer = styled.div`
  overflow: hidden;

  .programTitle {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

const SubtitleContainer = styled.div`
  padding-top: 0px;
`;
